import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Button,
  IconButton,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { useConfirm } from 'material-ui-confirm';
import { useTranslation } from 'react-i18next';
import CostModal from './CostModal';
import { createAuditorVisitCost } from '../api/createAuditorVisitCost';
import { updateAuditorVisitCostId } from '../api/updateAuditorVisitCostById';
import { deleteAuditorVisitCostById } from '../api/deleteAuditorVisitCostById';
import { TeamCost } from '../types';
import { useAuditorVisitDetailContext } from '../contexts/AuditorVisitDetailContext';
import MyCostsFilesModal from './MyCostsFilesModal';
import { getBeautifiedLocalDate, getBeautifiedLocalDateTime, useAuthStore } from '@/core';

const MyCosts = () => {
  const confirm = useConfirm();
  const { t } = useTranslation();
  const { auditorVisit, invalidateAuditorVisit } = useAuditorVisitDetailContext();
  const authStore = useAuthStore();
  const [filesScreen, setFilesScreen] = useState<'file-list' | 'add-files'>('file-list');
  const [addCostModal, setAddCostModal] = useState({ isOpen: false });
  const [editCostModal, setEditCostModal] = useState<{
    isOpen: boolean;
    title: string;
    visitCost?: TeamCost;
  }>({ isOpen: false, title: '', visitCost: undefined });
  const [filesModal, setFilesModal] = useState({ isOpen: false });

  const totalApproved = useMemo(() => {
    if (!auditorVisit.myCosts.costs) {
      return 0;
    }

    return auditorVisit.myCosts.costs.reduce((previousValue, currentValue) => {
      if (currentValue.approved) {
        const newValue = previousValue + Number(currentValue.approved);
        return newValue;
      }

      return previousValue;
    }, 0);
  }, [auditorVisit.myCosts.costs]);

  const addCostMutation = useMutation({
    mutationFn: createAuditorVisitCost,
    onSuccess: () => {
      invalidateAuditorVisit();
      setAddCostModal({ isOpen: false });
      setFilesScreen('add-files');
      setFilesModal({ isOpen: true });
    },
  });

  const updateCostMutation = useMutation({
    mutationFn: updateAuditorVisitCostId,
    onSuccess: () => {
      invalidateAuditorVisit();
      setEditCostModal({ isOpen: false, title: '', visitCost: undefined });
    },
  });

  const deleteCostMutation = useMutation({
    mutationFn: deleteAuditorVisitCostById,
    onSuccess: () => {
      invalidateAuditorVisit();
    },
  });

  const handleAddNewCost = () => {
    setAddCostModal({ isOpen: true });
  };

  const getProposalAmountLabel = (forAuditor?: boolean) => {
    if (forAuditor) {
      return t('Price', { defaultValue: 'Price' });
    }
    return t('Proposal amount', { defaultValue: 'Proposal amount' });
  };
  const getOpisLabel = (costName: string | undefined, isDaily: boolean | undefined): string => {
    if (isDaily)
      return t('Description of per diems', {
        defaultValue: 'Description of per diems',
      });
    if (costName === 'Prevoženi kilometri') return t('Relation', { defaultValue: 'Relation' });
    if (costName === 'Dnevnice')
      return t('Description of per diems', {
        defaultValue: 'Description of per diems',
      });
    return t('Note', { defaultValue: 'Note' });
  };
  return (
    <>
      <CostModal
        title={t('Add a new cost', { defaultValue: 'Add a new cost' })}
        isOpen={addCostModal.isOpen}
        minDate={auditorVisit.datStart}
        maxDate={auditorVisit.datEnd}
        setIsOpen={(isOpen) => setAddCostModal((previous) => ({ ...previous, isOpen }))}
        onSubmit={(data) =>
          addCostMutation.mutate({
            visitId: auditorVisit.id,
            data: {
              info: data.info,
              note: data.note,
              datReg: data.datReg,
              price: data.price,
              costListId: data.costList?.id,
              travelFrom: data.travelFrom ? new Date(data.travelFrom) : undefined,
              travelTo: data.travelTo ? new Date(data.travelTo) : undefined,
              amount: data.amount,
              lunchFactor: data.lunchFactor,
              proposedQ: data.proposedQ,
            },
          })
        }
      />
      <CostModal
        title={editCostModal.title}
        isOpen={editCostModal.isOpen}
        setIsOpen={(isOpen) => setEditCostModal((previous) => ({ ...previous, isOpen }))}
        teamCost={editCostModal.visitCost}
        minDate={auditorVisit.datStart}
        maxDate={auditorVisit.datEnd}
        onSubmit={(data) =>
          updateCostMutation.mutate({
            visitCostId: editCostModal.visitCost?.id as string,
            data: {
              info: data.info,
              note: data.note,
              datReg: data.datReg,
              price: data.price,
              costListId: data.costList?.id,
              travelFrom: data.travelFrom ? new Date(data.travelFrom) : undefined,
              travelTo: data.travelTo ? new Date(data.travelTo) : undefined,
              amount: data.amount,
              lunchFactor: data.lunchFactor,
              proposedQ: data.proposedQ,
            },
          })
        }
      />
      <MyCostsFilesModal
        show={filesModal.isOpen}
        setShow={(isOpen) => {
          setFilesModal({ isOpen });
        }}
        setScreen={setFilesScreen}
        screen={filesScreen}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Button variant="contained" onClick={handleAddNewCost} fullWidth>
          {t('Add new cost', { defaultValue: 'Add new cost' })}
        </Button>
        <Button variant="contained" onClick={() => setFilesModal({ isOpen: true })} fullWidth>
          {t('Evidences', { defaultValue: 'Evidences' })}
        </Button>
      </Box>
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ px: '1rem' }}>
          {/* <Typography>
            {t('Total price', { defaultValue: 'Total price' })}{' '}
            {totalPrice.toFixed(2).toString().replace('.', ',')}
          </Typography> */}
          <Typography>
            {t('Total approved', { defaultValue: 'Total approved' })}:{' '}
            {totalApproved.toFixed(2).toString().replace('.', ',')}
          </Typography>
        </Box>
        <List
          sx={{
            display: 'grid',
            gap: '0.5rem',
            'grid-template-columns': 'repeat(3, 1fr)',
          }}
        >
          {!!auditorVisit.myCosts.costs &&
            auditorVisit.myCosts.costs.map((cost) => (
              <ListItem
                key={cost.id}
                sx={{
                  display: 'flex',
                  margin: '0.5rem',
                  justifyContent: 'flex-start',
                  maxWidth: 'sm',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  height: '10rem',
                }}
                secondaryAction={
                  !cost.isFeeConf &&
                  !cost.isConfirmedByLeader && (
                    <>
                      <Tooltip title={t('Edit', { defaultValue: 'Edit' })} placement="top">
                        <IconButton
                          onClick={() =>
                            setEditCostModal({
                              title: `${t('Edit cost', { defaultValue: 'Edit cost' })} - ${
                                cost.costList?.name
                              }`,
                              isOpen: true,
                              visitCost: cost,
                            })
                          }
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {authStore.user?.user.id === cost.author && (
                        <Tooltip title={t('Delete', { defaultValue: 'Delete' })} placement="top">
                          <IconButton
                            onClick={() => {
                              confirm({
                                description: `${t('This will permamently delete', {
                                  defaultValue: 'This will permamently delete',
                                })} ${cost.info}.`,
                              }).then(() => deleteCostMutation.mutate({ visitCostId: cost.id }));
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )
                }
              >
                <ListItemText
                  primaryTypographyProps={{ fontSize: '1.25rem' }}
                  secondaryTypographyProps={{ fontSize: '1rem' }}
                  primary={cost?.costList?.name || 'N/A'}
                  secondary={
                    <Box>
                      <Typography>
                        {getOpisLabel(cost?.costList?.name, cost?.costList?.isDaily)}: {cost.note}
                      </Typography>
                      {!cost.costList?.isDaily &&
                        !cost.costList?.forAuditor &&
                        (cost.isConfirmedByLeader ? (
                          <>
                            <Typography>
                              {t('Approved', { defaultValue: 'Approved' })}:{' '}
                              {cost.approvedQ ? cost.approvedQ.toString().replace('.', ',') : 'N/A'}
                            </Typography>
                            <Typography>
                              {getProposalAmountLabel(cost.costList?.forAuditor)}:{' '}
                              {cost.proposedQ ? cost.proposedQ.toString().replace('.', ',') : 'N/A'}
                            </Typography>
                          </>
                        ) : (
                          <Typography>
                            {getProposalAmountLabel(cost.costList?.forAuditor)}:{' '}
                            {cost.proposedQ ? cost.proposedQ.toString().replace('.', ',') : 'N/A'}
                          </Typography>
                        ))}
                      {!cost.costList?.isDaily &&
                        cost.costList?.forAuditor &&
                        (cost.isConfirmedByLeader ? (
                          <>
                            <Typography>
                              {t('Approved', { defaultValue: 'Approved' })}:{' '}
                              {cost.approvedQ && cost.price
                                ? cost.price.toString().replace('.', ',')
                                : 'N/A'}
                            </Typography>
                            <Typography>
                              {getProposalAmountLabel(cost.costList?.forAuditor)}:{' '}
                              {cost.price ? cost.price.toString().replace('.', ',') : 'N/A'}
                            </Typography>
                          </>
                        ) : (
                          <Typography>
                            {getProposalAmountLabel(cost.costList?.forAuditor)}:{' '}
                            {cost.price ? cost.price.toString().replace('.', ',') : 'N/A'}
                          </Typography>
                        ))}
                      {cost.costList?.isDaily && !cost.costList?.forAuditor && (
                        <>
                          <Typography>
                            {t('Travel from', { defaultValue: 'Travel from' })}:{' '}
                            {cost.travelFrom ? getBeautifiedLocalDateTime(cost.travelFrom) : 'N/A'}
                          </Typography>
                          <Typography>
                            {t('Travel to', { defaultValue: 'Travel to' })}:{' '}
                            {cost.travelTo ? getBeautifiedLocalDateTime(cost.travelTo) : 'N/A'}
                          </Typography>
                          <Typography>
                            {t('Lunch', { defaultValue: 'Lunch' })}:{' '}
                            {cost.lunchFactor
                              ? t('Yes', { defaultValue: 'Yes' })
                              : t('No', { defaultValue: 'No' })}
                          </Typography>
                        </>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
        </List>
      </Box>
    </>
  );
};

export default MyCosts;
